import {
  Box,
  FormControl,
  FormHelperText,
  InputAdornment,
  OutlinedInput as MuiOutlinedInput,
  OutlinedInputProps,
  IconButton,
} from "@mui/material";
import React, { useRef, useState } from "react";
import useTheme from "../../../../common/hooks/useTheme";
import styled from "styled-components";
import {
  getBaseInputStyles,
  BaseStyledInputLabel,
  INPUT_PADDING,
  LABEL_TRANSFORM,
} from "./BaseInputStyles";

const ClearIcon = ({ theme }: { theme: any }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
      fill={theme.colors_ds.NEUTRAL_ICON_WEAK}
    />
  </svg>
);

interface StyledOutlinedInputProps extends OutlinedInputProps {
  has_error?: string | undefined;
  input_type?: "numeric" | "text" | "double-numeric";
  has_label?: boolean;
  has_unit?: boolean;
  is_multiline?: boolean;
  full_height?: boolean;
  has_max_length?: boolean;
}

const StyledOutlinedInput = styled(MuiOutlinedInput).withConfig({
  shouldForwardProp: (prop) =>
    ![
      "has_error",
      "input_type",
      "has_label",
      "has_unit",
      "is_multiline",
      "full_height",
      "has_max_length",
    ].includes(prop as string),
})<StyledOutlinedInputProps>(
  ({
    theme,
    input_type,
    has_label,
    has_unit,
    is_multiline,
    full_height,
    has_max_length,
    has_error,
  }) => ({
    // Base styles from shared component
    ...getBaseInputStyles(theme, has_error),
    ...(full_height
      ? {
          height: "100%",
          "& .MuiInputBase-root": {
            height: "100%",
            alignItems: "stretch",
            padding: "0",
          },
          "& .MuiInputBase-inputMultiline": {
            height: "100%",
            padding: has_label ? "52px 12px 16px 12px" : "16px 12px",
            lineHeight: "20px",
            resize: "none",
            overflow: "auto",
            width: input_type === "numeric" ? "30%" : "100%",
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
              borderRadius: "8px",
            },
          },
          "& .MuiOutlinedInput-root": {
            height: "100%",
            padding: "0",
          },
        }
      : is_multiline
      ? {
          height: has_max_length ? "auto" : "auto",
          minHeight: "56px",
          padding: "0 !important",
          "& .MuiInputBase-root": {
            height: has_max_length ? "auto" : "auto",
            alignItems: "stretch",
            padding: "0 !important",
          },
          "& .MuiInputBase-inputMultiline": {
            padding: has_label ? "38px 12px 19px 12px" : "16px 12px",
            lineHeight: "20px",
            resize: "none",
            overflow: has_max_length ? "auto" : "hidden",
            transition: "none",
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
              borderRadius: "8px",
            },
          },
          "& .MuiOutlinedInput-root": {
            height: has_max_length ? "auto" : "auto",
            padding: "0 !important",
            transition: "none",
          },
        }
      : {
          height: has_max_length ? "auto" : "56px",
          minHeight: "56px",
          "& .MuiOutlinedInput-input": {
            marginTop: has_label ? "16px" : "0px",
            width: input_type === "numeric" ? "30%" : "100%",
          },
        }),
    // Override input padding based on label and max_length
    "& .MuiOutlinedInput-input": {
      ...getBaseInputStyles(theme, has_error)["& .MuiOutlinedInput-input"],
      width: input_type === "numeric" && has_unit ? "30%" : "100%",
      padding: has_max_length
        ? has_label
          ? "4px 0 0 0"
          : "0"
        : has_label
        ? INPUT_PADDING.withLabel
        : INPUT_PADDING.withoutLabel,
      ...(has_max_length && {
        wordWrap: "break-word",
        wordBreak: "break-word",
        whiteSpace: "pre-wrap",
      }),
    },
  })
);

// Using BaseStyledInputLabel from BaseInputStyles

const StyledFormHelperText = styled(FormHelperText)`
  width: 500px;
  font-family: var(--font-Medium), sans-serif;
  font-size: 10px;
  position: absolute;
  bottom: -24px;
  left: -16px;
  background-color: transparent;
  color: ${(props) => props.theme.colors_ds.DANGER_GRAPH_DEFICIT};
`;

const TextInputField = ({
  label,
  placeholder,
  unit,
  handleChange,
  value,
  error,
  setFocusedIndex = () => {},
  name = "",
  maxLength,
  showCharacterCount = false,
  multiline = false,
  maxRows = 4,
  fullHeight = false,
  showClearButton = false,
  inputRef,
  disabled = false,
  endAdornment,
}: {
  label?: string;
  placeholder?: string;
  unit?: string;
  handleChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  value?: string;
  error?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
  setFocusedIndex?: (index: string | null) => void;
  name?: string;
  maxLength?: number;
  showCharacterCount?: boolean;
  multiline?: boolean;
  maxRows?: number;
  fullHeight?: boolean;
  showClearButton?: boolean;
  disabled?: boolean;
  // Extra content at the right end of the input (an info icon, a badge),
  // shown before the clear button and unit.
  endAdornment?: React.ReactNode;
}) => {
  const theme = useTheme();
  const [focused, setFocused] = useState(false);
  const internalInputRef = useRef<HTMLInputElement>(null);

  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      onSubmit={(e) => e.preventDefault()}
      sx={{
        flex: fullHeight ? 1 : "none",
        width: "100%",
        height: fullHeight ? "100%" : "auto",
      }}
    >
      <FormControl
        variant="outlined"
        fullWidth
        sx={{
          position: "relative",
          height: fullHeight ? "100%" : maxLength ? "auto" : "56px",
          minHeight: fullHeight ? "100%" : "56px",
        }}
      >
        {label && (
          <BaseStyledInputLabel
            htmlFor={label}
            theme={theme}
            style={{
              transform: Boolean(value || focused || placeholder)
                ? LABEL_TRANSFORM.shrunk
                : LABEL_TRANSFORM.normal,
            }}
          >
            {label}
          </BaseStyledInputLabel>
        )}

        <StyledOutlinedInput
          id={label}
          value={value || ""}
          placeholder={placeholder}
          type="text"
          multiline={multiline || fullHeight || Boolean(maxLength)}
          minRows={
            fullHeight ? 1 : multiline ? 2 : Boolean(maxLength) ? 1 : undefined
          }
          maxRows={
            fullHeight
              ? undefined
              : multiline || Boolean(maxLength)
              ? maxRows
              : undefined
          }
          inputRef={inputRef || internalInputRef}
          inputProps={{
            maxLength,
          }}
          disabled={disabled}
          onFocus={() => {
            setFocused(true);
            setFocusedIndex(name);
          }}
          onBlur={() => {
            setFocused(false);
            setFocusedIndex(null);
          }}
          onChange={handleChange}
          theme={theme}
          has_error={error ? "true" : undefined}
          input_type={undefined}
          has_label={Boolean(label)}
          has_unit={Boolean(unit)}
          is_multiline={multiline || Boolean(maxLength)}
          full_height={fullHeight}
          has_max_length={Boolean(maxLength)}
          endAdornment={
            <>
              {endAdornment && (
                <InputAdornment position="end" sx={{ marginLeft: "8px" }}>
                  {endAdornment}
                </InputAdornment>
              )}
              {showClearButton && value && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => {
                      handleChange({
                        target: { name, value: "" },
                      } as React.ChangeEvent<HTMLInputElement>);
                    }}
                    edge="end"
                    size="small"
                    sx={{
                      color: theme.colors_ds.NEUTRAL_ICON_WEAK,
                      "&:hover": {
                        color: theme.colors_ds.NEUTRAL_ICON_DEFAULT,
                      },
                    }}
                  >
                    <ClearIcon theme={theme} />
                  </IconButton>
                </InputAdornment>
              )}
              {unit && (value || focused) && (
                <InputAdornment
                  position="end"
                  sx={{ marginTop: "16px", marginLeft: "8px" }}
                >
                  <span
                    style={{
                      fontFamily: "var(--font-Medium)",
                      fontSize: "14px",
                      color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
                    }}
                  >
                    {unit}
                  </span>
                </InputAdornment>
              )}
            </>
          }
        />

        {error && error !== "error" && (
          <StyledFormHelperText theme={theme}>{error}</StyledFormHelperText>
        )}

        {showCharacterCount && maxLength && (
          <Box
            sx={{
              position: "absolute",
              bottom: "-20px",
              right: "0px",
              fontSize: "10px",
              fontFamily: "var(--font-Medium)",
              color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
            }}
          >
            <span style={{ fontFamily: "var(--font-Bold)" }}>
              {value?.length || 0}
            </span>
            /{maxLength}
          </Box>
        )}
      </FormControl>
    </Box>
  );
};

export default TextInputField;
